/**
 * ノード型の型バッジ。
 *
 * 色は型ごとに 1 色を割り当てず、**層**（分類マスター / 業務 / 画面 / 用語）で 4 色に畳む。
 * 型の数だけの色は共有デザインシステムの semantic color（success / warning / error / info / accent）を
 * 超え、色の意味が場当たりになる。層で畳めば「同じ色 = 同じ層」が保たれ、検索結果のような
 * 型が混ざる一覧で層の切り替わりが読み取れる。
 *
 * ステージは分類マスターの層、業務フローと業務ルールは業務の層に置く。
 * 動詞と単位は**書込の経路こそ分類マスターだが、読む側から見れば語彙**なので用語と同じ層に置く。
 *
 * `warning` / `error` は違反・宙吊りの表現に予約しているため、型バッジには使わない。
 * 型が並ぶだけの一覧が常時警告色になると、本当の残 TODO が埋もれる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 仕様グラフのノード型を層ごとの色分けバッジとして描く。
 * @graph-connects none
 */
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { useTranslation } from 'react-i18next';

import { nodeTypeLabelKey, type SpecNodeTypeValue } from '../../lib/spec-node-types.js';

/** ノード型ごとの層の色。表示名は `nodeTypeLabelKey` が決める。 @graph-connects none */
const NODE_TYPE_VIEW: Record<SpecNodeTypeValue, { className: string }> = {
  Actor: {
    className: 'text-[var(--color-info)] border-[var(--color-info)]',
  },
  Domain: {
    className: 'text-[var(--color-info)] border-[var(--color-info)]',
  },
  Stage: {
    className: 'text-[var(--color-info)] border-[var(--color-info)]',
  },
  Flow: {
    className: 'text-[var(--color-accent-primary)] border-[var(--color-accent-primary)]',
  },
  Business: {
    className: 'text-[var(--color-accent-primary)] border-[var(--color-accent-primary)]',
  },
  Screen: {
    className: 'text-[var(--color-success)] border-[var(--color-success)]',
  },
  Component: {
    className: 'text-[var(--color-success)] border-[var(--color-success)]',
  },
  Rule: {
    className: 'text-[var(--color-accent-primary)] border-[var(--color-accent-primary)]',
  },
  Term: {
    className: 'text-[var(--color-text-secondary)] border-[var(--color-border)]',
  },
  Verb: {
    className: 'text-[var(--color-text-secondary)] border-[var(--color-border)]',
  },
  Unit: {
    className: 'text-[var(--color-text-secondary)] border-[var(--color-border)]',
  },
};

/** `NodeTypeBadge` の props。 */
export interface NodeTypeBadgeProps {
  /** 仕様グラフのノード型。 */
  nodeType: SpecNodeTypeValue;
}

/**
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ノード型を層ごとの色分けバッジで描く。
 * @graph-connects none
 */
export function NodeTypeBadge({ nodeType }: NodeTypeBadgeProps) {
  const { t } = useTranslation();
  const view = NODE_TYPE_VIEW[nodeType];

  return (
    <Badge variant="default" size="sm" className={view.className} data-node-type={nodeType}>
      {t(nodeTypeLabelKey(nodeType))}
    </Badge>
  );
}
